<template>
  <main-layout>
    <div>
      <v-row no-gutters align="center" class="content-header">
        <span class="font-weight-bold">Visites utilisateurs</span>
        <v-spacer></v-spacer>
      </v-row>
      <div class="page-main-content">
        <v-col class="py-2 mb-4 search-bar border-component">
          <v-text-field
            v-model="searchString"
            placeholder="Rechercher un utilisateur..."
            prepend-icon="search"
            hide-details
            dense
            clearable
            class="input-no-b-line"
          />
        </v-col>
        <div>
          <v-row no-gutters justify="center" align="center" class="py-8" style="min-height: 100px;" v-if="isFetchingUsers">
            <div style="width: 250px;">
              <v-progress-linear
                color="primary"
                indeterminate
                rounded
                height="6"
              ></v-progress-linear>
            </div>
          </v-row>
          <v-row no-gutters justify="center" align="center" class="pa-4 text--secondary" style="min-height: 100px;" v-else-if="!filteredUsers.length">
            Aucun utilisateur
          </v-row>
          <template v-else>
            <div class="pt-1 pb-5">
              <v-row no-gutters justify="center" align="center">
                <h3 class="pl-2">
                  {{ filteredUsers.length }} {{ `utilisateur` | pluralize(filteredUsers.length) }} {{ `trouvé` | pluralize(filteredUsers.length) }}
                </h3>
                <v-spacer />
                <div>
                  <v-row no-gutters justify="center" align="center">
                    <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text" @click="previousPage">
                      <v-icon>chevron_left</v-icon>
                    </v-btn>
                    <div class="ml-2 primary--text font-weight-bold">
                      {{ currentPage + 1 }} / {{ nbPages }}
                    </div>
                    <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text ml-2" @click="nextPage">
                      <v-icon>chevron_right</v-icon>
                    </v-btn>
                  </v-row>
                </div>
              </v-row>
            </div>
            <v-list class="pa-0 border-component">
              <template v-for="(user, index) in filteredUsersPaginated">
                <v-list-item :key="`user-${index}`">
                  <v-list-item-avatar>
                    <v-icon>account_circle</v-icon>
                  </v-list-item-avatar>
                  <v-row no-gutters justify="center" align="center">
                    <v-col align="left">
                      <v-list-item-content>
                        <v-list-item-title>{{ user.firstname }} {{ user.lastname }}</v-list-item-title>
                        <v-list-item-subtitle class="pt-1">
                          <span v-if="user.username">{{ user.username }} ({{ user.email || `aucun email` }})</span>
                          <span v-else>{{ user.email }} </span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-col>
                    <div class="my-3 ml-3">
                      <div class="text-right">
                        <v-chip small outlined color="primary darken-1" text-color="primary darken-1">
                          {{ user.client.name }}
                        </v-chip>
                      </div>
                      <div class="pt-2 text-right">
                        <v-chip small color="light-blue" text-color="white">
                          {{ user.nbVisits }} visites
                        </v-chip>
                      </div>
                    </div>
                  </v-row>
                </v-list-item>
                <v-divider
                  v-if="index + 1 < filteredUsersPaginated.length"
                  :key="index"
                ></v-divider>
              </template>
            </v-list>
            <div class="pa-4">
              <v-row no-gutters justify="center" align="center">
                <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text" @click="previousPage">
                  <v-icon>chevron_left</v-icon>
                </v-btn>
                <div class="ml-2 primary--text font-weight-bold">
                  {{ currentPage + 1 }} / {{ nbPages }}
                </div>
                <v-btn class="n-white-btn n-small-btn n-icon-btn primary--text ml-2" @click="nextPage">
                  <v-icon>chevron_right</v-icon>
                </v-btn>
              </v-row>
            </div>
          </template>
        </div>
      </div>
    </div>
  </main-layout>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import MainLayout from '@/views/indeegoSettings/MainLayout.vue';

const PAGE_SIZE = 25;

export default {
  data: () => ({
    isFetchingUsers: false,
    searchString: ``,
    currentPage: 0,
  }),
  computed: {
    ...mapState(`ui/indeegoSettingsUserVisits`, [
      `userVisits`,
    ]),
    flavoredUsers() {
      return _.chain(this.userVisits)
        .map(user => ({
          ...user,
          $searchString: _.toLower(`
            ${user.firstname} ${user.lastname} ${user.username || ``} ${user.email || ``} ${user.client.name}
          `),
        }))
        .sortBy(`$searchString`)
        .value();
    },
    filteredUsers() {
      let users = this.flavoredUsers;
      if (this.searchString) {
        this.searchString.split(` `).forEach(searchStringPart => {
          searchStringPart = _.toLower(searchStringPart);
          if (searchStringPart.startsWith(`visits>`)) {
            const minNbVisits = parseInt(searchStringPart.split(`visits>`)[1], 10) || 0;
            users = _.filter(users, user => user.nbVisits > minNbVisits);
          } else if (searchStringPart.startsWith(`visits<`)) {
            const maxNbVisits = parseInt(searchStringPart.split(`visits<`)[1], 10) || 0;
            users = _.filter(users, user => user.nbVisits < maxNbVisits);
          } else if (searchStringPart.startsWith(`visits=`)) {
            const nbVisits = parseInt(searchStringPart.split(`visits=`)[1], 10) || 0;
            users = _.filter(users, user => user.nbVisits === nbVisits);
          } else {
            users = _.filter(users, user =>
              user.$searchString.indexOf(searchStringPart) !== -1
            );
          }
        });
      }

      return users;
    },
    filteredUsersPaginated() {
      const firstIndex = this.currentPage * PAGE_SIZE;
      return this.filteredUsers.slice(firstIndex, firstIndex + PAGE_SIZE);
    },
    nbPages() {
      return Math.ceil(this.filteredUsers.length / PAGE_SIZE);
    },
  },
  methods: {
    ...mapActions(`ui/indeegoSettingsUserVisits`, [
      `fetchUserVisits`,
    ]),
    async fetchUsers() {
      this.isFetchingUsers = true;
      await this.fetchUserVisits();
      this.isFetchingUsers = false;
    },
    previousPage() {
      if (this.currentPage > 0) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      const newPageIndex = (this.currentPage + 1) * PAGE_SIZE;
      if (newPageIndex < this.filteredUsers.length) {
        this.currentPage += 1;
      }
    },
  },
  beforeMount() {
    this.fetchUsers();
  },
  components: {
    MainLayout,
  },
};
</script>

<style lang="scss">
</style>
